import type { ReactNode } from "react";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router";

import { FilterButton, InfinityLoader, MasterDetailLayout } from "common/components";
import { getPageId } from "common/utilities";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useGo } from "common/utilities/useGo";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import { applyInitiativesSearchCriteria } from "domains/initiatives/actions";
import { InitiativeDetail } from "domains/initiatives/components/InitiativeDetail";
import { InitiativeListItem } from "domains/initiatives/components/InitiativeListItem";
import {
  createInitiativesForTopicSelector,
  getInitiativesFilterCount,
  getInitiativesSearchCriteria,
  getIsInitiativesFiltered,
} from "domains/initiatives/selectors";
import { activateModal } from "domains/modal/actions";
import { getTopicPageEvent, getTopicRelatedItemDetailPageEvent } from "domains/topic/analytics";
import { getTopicTitle, useTopicNavigation } from "domains/topicDetail/utils";
import { TopicSearchBar } from "domains/topicSearchFilter/components/TopicSearchBar";
import { useTopicContentMeta } from "domains/topicSearchFilter/useTopicContentMeta";
import { fetchInitiativeById } from "entities/initiatives/actions";
import { createInitiativeSelector, createInitiativesSelector } from "entities/initiatives/selectors";
import { getCanAccessTopicInitiatives } from "entities/permissions/selectors";
import { fetchTopic } from "entities/topics/actions";
import { createTopicSelector, getTopic } from "entities/topics/selectors";

export const TopicInitiativePage = () => {
  const dispatch = useDispatch();
  const go = useGo();
  const { initiativeId, keyIssueId, topicId } = useParams();
  const initiativeAccess = useSelector(getCanAccessTopicInitiatives);
  const filterCount = useSelector(getInitiativesFilterCount);
  const { search } = useSelector(getInitiativesSearchCriteria);
  const isFiltered = useSelector(getIsInitiativesFiltered);
  const { hasFilters } = useTopicContentMeta("initiatives");
  const track = useTrack();
  const translate = useTranslate();

  if (topicId === undefined) throw new Error("topicId param undefined in TopicInitiativePage");
  const keyIssue = useSelector(getTopic(keyIssueId));
  const topic = useSelector(createTopicSelector(topicId));
  const { path } = useTopicNavigation({ id: topicId, keyIssueId });

  const selectedTopic = keyIssueId ? keyIssue : topic;
  const selectedTopicId = keyIssueId ?? topicId;

  const initiativesForTopic = useSelector(createInitiativesForTopicSelector(selectedTopicId));

  const initiativesSelector = useMemo(
    () => createInitiativesSelector(initiativesForTopic?.data ?? []),
    [initiativesForTopic?.data],
  );

  const initiatives = useSelector(initiativesSelector);

  const id = getPageId(initiativeId, initiatives);

  const initiative = useSelector(createInitiativeSelector(id));

  const hasNextPage = initiativesForTopic?.hasNextPage ?? true;
  const isPending = initiativesForTopic?.isFetching ?? false;

  useEffect(() => {
    if (id) {
      dispatch(fetchInitiativeById({ id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!topic || (keyIssueId && !keyIssue)) {
      return;
    }
    const event = getTopicPageEvent(topic, keyIssue, "Initiative List");
    track(event);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyIssue?.id, topic?.id, track]);

  useEffect(() => {
    if (!selectedTopic || !initiative) {
      return;
    }
    const event = getTopicRelatedItemDetailPageEvent("Project detail", initiative, "Project", selectedTopic);
    track(event);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initiative?.id, selectedTopic?.id, track]);

  useEffect(() => {
    if (initiatives.length === 0) {
      dispatch(
        applyInitiativesSearchCriteria({
          topicId: selectedTopicId,
          offset: 0,
          limit: 20,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopicId]);

  useEffect(() => {
    if (!selectedTopic) {
      dispatch(fetchTopic({ id: topicId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopicId]);

  useEffect(() => {
    if (!initiativeAccess) {
      go("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initiativeAccess]);

  const getLink = (initiativeId: string) => `${path}/initiatives/${initiativeId}`;

  const loadNextPage = () => {
    dispatch(
      applyInitiativesSearchCriteria({
        topicId: selectedTopicId,
        offset: initiatives.length,
        limit: 20,
      }),
    );
  };

  let emptyMessage = isFiltered
    ? translate("app.discover.topic.tabs.initiatives.no_data")
    : `${translate("app.topic.no_content")} ${translate("app.discover.topic.tabs.initiatives.title")}.`;

  if (!topic?.id) {
    emptyMessage = translate("app.no_access.topic_content");
  }

  let list: ReactNode;

  if (initiatives.length === 0 && !isPending) {
    list = <p>{emptyMessage}</p>;
  } else {
    list = (
      <InfinityLoader hasNextPage={hasNextPage} isPending={isPending} loadNextPage={loadNextPage}>
        {initiatives.map((item) => (
          <InitiativeListItem
            key={item.id}
            initiative={item}
            isSelected={initiativeId === item.id}
            link={getLink(item.id)}
          />
        ))}
      </InfinityLoader>
    );
  }
  const onFilterClick = () =>
    dispatch(activateModal({ id: "TopicInitiativeFilter", params: { selectedTopicId, topicId } }));

  const topicFilterButton =
    topic?.id && hasFilters ? <FilterButton onClick={onFilterClick} count={filterCount} /> : undefined;

  const onSearchHandle = (search: string) =>
    dispatch(applyInitiativesSearchCriteria({ topicId: selectedTopicId, search }));

  const searchElement = topic?.id ? (
    <TopicSearchBar onSearch={onSearchHandle} isPending={isPending} defaultValue={search} />
  ) : undefined;

  return (
    <MasterDetailLayout
      headerElement={searchElement}
      listTitle={translate("app.discover.topic.tabs.initiatives.title")}
      listComponent={list}
      listBackNavigation={{
        label: getTopicTitle(topic, keyIssue) ?? "",
        path,
      }}
      detailBackNavigation={{
        label: translate("app.discover.topic.tabs.initiatives.title"),
        path: `${path}/initiatives`,
      }}
      mainView={initiativeId ? "right" : "left"}
      listHeaderRightElement={topicFilterButton}
    >
      <InitiativeDetail initiative={initiative} shareLink={`/topics/${topicId}/initiatives/${id}`} />
    </MasterDetailLayout>
  );
};
